import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import {
  formatDurationSince,
  formatRelativeTime,
  getStatusLabel,
  getStatusVariant,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type UptimeViewHeaderProps = ComponentProps<"div"> & {
  target: UptimeTargetFragment
}

// Three at-a-glance stat tiles: current streak, last check, 90d
// down ratio. Each tile follows the same label / value / sub layout
// so the eye lands consistently across them.
function UptimeViewHeader(props: UptimeViewHeaderProps) {
  const { target, className, ...divProps } = props

  const statusLabel = getStatusLabel(target.LatestStatus)
  const statusVariant = getStatusVariant(target.LatestStatus)
  const isHealthy = target.LatestStatus === "up"
  const streakLabel = isHealthy ? "Currently up for" : "Currently down for"
  const streakValue = formatDurationSince(target.CurrentStatusSince)

  const lastSub =
    target.LastDurationMs != null
      ? `${target.LastDurationMs} ms${target.LastHttpStatus ? ` · HTTP ${target.LastHttpStatus}` : ""}`
      : null

  const downSub =
    target.AvgDurationMs90d != null
      ? `avg response ${target.AvgDurationMs90d} ms`
      : "no successful probes yet"

  return (
    <div className={cn("uptime-view-stat-grid", className)} {...divProps}>
      <div className="uptime-view-stat-card">
        <div className="uptime-view-stat-label flex items-center gap-2">
          <Dot variant={statusVariant} />
          <span>{statusLabel}</span>
        </div>
        <div className="uptime-view-stat-value">{streakValue}</div>
        <div className="uptime-view-stat-sub">{streakLabel}</div>
      </div>

      <div className="uptime-view-stat-card">
        <div className="uptime-view-stat-label">Last checked</div>
        <div className="uptime-view-stat-value">
          {formatRelativeTime(target.LastCheckedAt)}
        </div>
        <div className="uptime-view-stat-sub">{lastSub ?? "—"}</div>
      </div>

      <div className="uptime-view-stat-card">
        <div className="uptime-view-stat-label">Down checks · 90 d</div>
        <div className="uptime-view-stat-value">
          {target.DownChecks90d}
          <span className="text-muted-foreground text-base font-medium ml-1">
            / {target.TotalChecks90d}
          </span>
        </div>
        <div className="uptime-view-stat-sub">{downSub}</div>
      </div>
    </div>
  )
}

export default UptimeViewHeader
